import { getSupabase } from '@/lib/supabase';
import type { PlayerGroup } from '@/schemas';
import type { UserId } from '@/schemas/ids';
import { decodeRowToPlayerGroup, type PlayerGroupRow } from '../codec';
import { getUserSyncState, setUserLastDownloadedAt } from '../state';

export interface DownloadPlayerGroupsResult {
  groups: PlayerGroup[];
}

export async function downloadRemotePlayerGroups(
  userId: UserId,
): Promise<DownloadPlayerGroupsResult> {
  const client = getSupabase();
  const {
    data: { session },
  } = await client.auth.getSession();
  if (!session?.user) {
    throw new Error('User must be authenticated to sync player groups');
  }

  const { lastDownloadedAt = 0 } = await getUserSyncState(userId, 'player_groups');
  const { data, error } = await client
    .from('player_groups')
    .select<'*', PlayerGroupRow>('*')
    .eq('user_id', userId)
    .or(
      `created_at.gt.${lastDownloadedAt},updated_at.gt.${lastDownloadedAt},deleted_at.gt.${lastDownloadedAt}`,
    )
    .order('updated_at', { ascending: true })
    .limit(1000);

  if (error) throw error;

  const groups = (data ?? []).map((row) => decodeRowToPlayerGroup(row));
  if (groups.length > 0) {
    const maxTs = Math.max(
      ...groups.map((group) => Math.max(group.createdAt, group.updatedAt, group.deletedAt ?? 0)),
      0,
    );
    await setUserLastDownloadedAt(userId, 'player_groups', maxTs);
  }

  return { groups };
}
